// Properties management page
// Reference: spec.md US7 - owner/manager manages properties

import { useState, useEffect, useRef } from 'react';
import { Button, Input, Select, FormField } from '../../components/ui';
import { api } from '../../services/api';
import type { Property, PropertyType, Unit } from '@propman/shared';

interface PropertyWithUnits extends Property {
  unitCount: number;
}

interface PropertyFormData {
  name: string;
  address: string;
  propertyType: PropertyType;
}

interface UnitFormData {
  identifier: string;
  floor: string;
  isCommonArea: boolean;
}

const propertyTypeLabels: Record<PropertyType, string> = {
  multi_unit_building: 'Multi-Unit Building',
  townhouse: 'Townhouse',
  villa: 'Villa',
  single_flat: 'Single Flat',
};

export function PropertiesPage() {
  const [properties, setProperties] = useState<PropertyWithUnits[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [showAddUnitModal, setShowAddUnitModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<PropertyWithUnits | null>(null);
  const [propertyUnits, setPropertyUnits] = useState<Unit[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);

  // Form states
  const [formData, setFormData] = useState<PropertyFormData>({
    name: '',
    address: '',
    propertyType: 'multi_unit_building',
  });
  const [unitFormData, setUnitFormData] = useState<UnitFormData>({
    identifier: '',
    floor: '',
    isCommonArea: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [uploadingImageFor, setUploadingImageFor] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadPropertyIdRef = useRef<string | null>(null);

  useEffect(() => {
    loadProperties();
  }, []);

  async function loadProperties() {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<{ properties: PropertyWithUnits[] }>('/properties');
      setProperties(response.properties);
    } catch (err) {
      console.error('Failed to load properties:', err);
      setError('Failed to load properties. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  async function loadPropertyUnits(propertyId: string) {
    setUnitsLoading(true);
    try {
      const response = await api.get<{ units: Unit[] }>('/units', { propertyId });
      setPropertyUnits(response.units);
    } catch (err) {
      console.error('Failed to load units:', err);
    } finally {
      setUnitsLoading(false);
    }
  }

  function openAddModal() {
    setFormData({ name: '', address: '', propertyType: 'multi_unit_building' });
    setFormError(null);
    setShowAddModal(true);
  }

  function openEditModal(property: PropertyWithUnits) {
    setSelectedProperty(property);
    setFormData({
      name: property.name,
      address: property.address,
      propertyType: property.propertyType,
    });
    setFormError(null);
    setShowEditModal(true);
  }

  function openUnitsModal(property: PropertyWithUnits) {
    setSelectedProperty(property);
    setShowUnitsModal(true);
    loadPropertyUnits(property.id);
  }

  function openAddUnitModal() {
    setUnitFormData({ identifier: '', floor: '', isCommonArea: false });
    setFormError(null);
    setShowAddUnitModal(true);
  }

  async function handleAddProperty(e: React.FormEvent) {
    e.preventDefault();
    setIsSaving(true);
    setFormError(null);

    try {
      await api.post('/properties', formData);
      await loadProperties();
      setShowAddModal(false);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setFormError(error.message || 'Failed to create property');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleEditProperty(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedProperty) return;

    setIsSaving(true);
    setFormError(null);

    try {
      await api.patch(`/properties/${selectedProperty.id}`, formData);
      await loadProperties();
      setShowEditModal(false);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setFormError(error.message || 'Failed to update property');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteProperty(property: PropertyWithUnits) {
    if (!confirm(`Are you sure you want to delete "${property.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/properties/${property.id}`);
      await loadProperties();
    } catch (err: unknown) {
      const error = err as { message?: string };
      alert(error.message || 'Failed to delete property');
    }
  }

  async function handleAddUnit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedProperty) return;

    setIsSaving(true);
    setFormError(null);

    try {
      await api.post('/units', {
        propertyId: selectedProperty.id,
        identifier: unitFormData.identifier,
        floor: unitFormData.floor || null,
        isCommonArea: unitFormData.isCommonArea,
      });
      await loadPropertyUnits(selectedProperty.id);
      await loadProperties();
      setShowAddUnitModal(false);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setFormError(error.message || 'Failed to create unit');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteUnit(unit: Unit) {
    if (!confirm(`Are you sure you want to delete unit "${unit.identifier}"?`)) {
      return;
    }

    try {
      await api.delete(`/units/${unit.id}`);
      if (selectedProperty) {
        await loadPropertyUnits(selectedProperty.id);
        await loadProperties();
      }
    } catch (err: unknown) {
      const error = err as { message?: string };
      alert(error.message || 'Failed to delete unit');
    }
  }

  function triggerImageUpload(propertyId: string) {
    uploadPropertyIdRef.current = propertyId;
    fileInputRef.current?.click();
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const propertyId = uploadPropertyIdRef.current;

    if (!file || !propertyId) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size must be less than 10MB');
      return;
    }

    setUploadingImageFor(propertyId);

    try {
      // Use the api client's uploadFile method which handles auth
      await api.uploadFile(`/properties/${propertyId}/image`, file);
      await loadProperties();
    } catch (err: unknown) {
      const error = err as { message?: string };
      alert(error.message || 'Failed to upload image');
    } finally {
      setUploadingImageFor(null);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      uploadPropertyIdRef.current = null;
    }
  }

  async function handleDeleteImage(propertyId: string) {
    if (!confirm('Are you sure you want to remove this image?')) {
      return;
    }

    setUploadingImageFor(propertyId);

    try {
      await api.delete(`/properties/${propertyId}/image`);
      await loadProperties();
    } catch (err: unknown) {
      const error = err as { message?: string };
      alert(error.message || 'Failed to remove image');
    } finally {
      setUploadingImageFor(null);
    }
  }

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Properties</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage your properties and units
          </p>
        </div>
        <Button onClick={openAddModal}>
          <svg className="w-5 h-5 mr-2 -ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Property
        </Button>
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
          <Button variant="secondary" size="sm" className="mt-2" onClick={loadProperties}>
            Try Again
          </Button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading properties...</p>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && properties.length === 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No properties yet</h3>
          <p className="mt-2 text-gray-600">Get started by adding your first property.</p>
          <Button className="mt-4" onClick={openAddModal}>
            Add Property
          </Button>
        </div>
      )}

      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
        aria-label="Upload property image"
      />

      {/* Properties grid */}
      {properties.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {properties.map((property) => (
            <div
              key={property.id}
              className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
            >
              {/* Property image */}
              <div className="h-40 bg-gray-100 relative group">
                {uploadingImageFor === property.id ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                  </div>
                ) : property.imageUrl ? (
                  <>
                    <img
                      src={property.imageUrl}
                      alt={property.name}
                      className="w-full h-full object-cover"
                    />
                    {/* Hover overlay with actions */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => triggerImageUpload(property.id)}
                          className="px-3 py-1.5 bg-white text-gray-700 text-sm rounded-md hover:bg-gray-100 transition-colors"
                        >
                          Change
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(property.id)}
                          className="px-3 py-1.5 bg-red-500 text-white text-sm rounded-md hover:bg-red-600 transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => triggerImageUpload(property.id)}
                    className="w-full h-full flex flex-col items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    <svg className="h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="mt-2 text-sm text-gray-400">Click to add image</span>
                  </button>
                )}
              </div>

              {/* Property info */}
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900">{property.name}</h3>
                <p className="mt-1 text-sm text-gray-500">{property.address}</p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {propertyTypeLabels[property.propertyType]}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    {property.unitCount} {property.unitCount === 1 ? 'unit' : 'units'}
                  </span>
                </div>

                {/* Actions */}
                <div className="mt-4 flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openUnitsModal(property)}
                  >
                    Manage Units
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal(property)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDeleteProperty(property)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Property Modal */}
      {showAddModal && (
        <Modal title="Add Property" onClose={() => setShowAddModal(false)}>
          <form onSubmit={handleAddProperty} className="space-y-4">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{formError}</p>
              </div>
            )}

            <FormField label="Property Name" htmlFor="name" required>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Sunset Apartments"
                required
              />
            </FormField>

            <FormField label="Address" htmlFor="address" required>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="e.g., 123 Main St, City"
                required
              />
            </FormField>

            <FormField label="Property Type" htmlFor="propertyType" required>
              <Select
                id="propertyType"
                value={formData.propertyType}
                onChange={(e) => setFormData({ ...formData, propertyType: e.target.value as PropertyType })}
              >
                {Object.entries(propertyTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </FormField>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isSaving}>
                Add Property
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Property Modal */}
      {showEditModal && selectedProperty && (
        <Modal title="Edit Property" onClose={() => setShowEditModal(false)}>
          <form onSubmit={handleEditProperty} className="space-y-4">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{formError}</p>
              </div>
            )}

            <FormField label="Property Name" htmlFor="edit-name" required>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </FormField>

            <FormField label="Address" htmlFor="edit-address" required>
              <Input
                id="edit-address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
              />
            </FormField>

            <FormField label="Property Type" htmlFor="edit-propertyType" required>
              <Select
                id="edit-propertyType"
                value={formData.propertyType}
                onChange={(e) => setFormData({ ...formData, propertyType: e.target.value as PropertyType })}
              >
                {Object.entries(propertyTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </FormField>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isSaving}>
                Save Changes
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Units Modal */}
      {showUnitsModal && selectedProperty && (
        <Modal
          title={`Units - ${selectedProperty.name}`}
          onClose={() => setShowUnitsModal(false)}
          size="lg"
        >
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                {propertyUnits.length} {propertyUnits.length === 1 ? 'unit' : 'units'}
              </p>
              <Button size="sm" onClick={openAddUnitModal}>
                Add Unit
              </Button>
            </div>

            {unitsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto" />
              </div>
            ) : propertyUnits.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No units yet. Add your first unit.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 border rounded-md">
                {propertyUnits.map((unit) => (
                  <div key={unit.id} className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{unit.identifier}</p>
                      <p className="text-sm text-gray-500">
                        {unit.floor ? `Floor ${unit.floor}` : 'No floor specified'}
                        {unit.isCommonArea && ' • Common Area'}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDeleteUnit(unit)}
                    >
                      Delete
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Add Unit Modal */}
      {showAddUnitModal && selectedProperty && (
        <Modal title="Add Unit" onClose={() => setShowAddUnitModal(false)}>
          <form onSubmit={handleAddUnit} className="space-y-4">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{formError}</p>
              </div>
            )}

            <FormField label="Unit Identifier" htmlFor="unit-identifier" required hint="e.g., Apt 101, Unit A, Suite 5">
              <Input
                id="unit-identifier"
                value={unitFormData.identifier}
                onChange={(e) => setUnitFormData({ ...unitFormData, identifier: e.target.value })}
                placeholder="e.g., Apt 101"
                required
              />
            </FormField>

            <FormField label="Floor" htmlFor="unit-floor" hint="Optional">
              <Input
                id="unit-floor"
                value={unitFormData.floor}
                onChange={(e) => setUnitFormData({ ...unitFormData, floor: e.target.value })}
                placeholder="e.g., 1, Ground, Basement"
              />
            </FormField>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="unit-common"
                checked={unitFormData.isCommonArea}
                onChange={(e) => setUnitFormData({ ...unitFormData, isCommonArea: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="unit-common" className="text-sm text-gray-700">
                This is a common area (lobby, gym, parking, etc.)
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setShowAddUnitModal(false)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isSaving}>
                Add Unit
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// Modal component
interface ModalProps {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  size?: 'md' | 'lg';
}

function Modal({ title, children, onClose, size = 'md' }: ModalProps) {
  const sizeClasses = size === 'lg' ? 'max-w-2xl' : 'max-w-md';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Modal */}
        <div className={`relative bg-white rounded-lg shadow-xl w-full ${sizeClasses} p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-500"
              onClick={onClose}
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export default PropertiesPage;
